import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "react-toastify";

import {
  cancelPendingOrderPayment,
  confirmPaymentForExistingOrder,
} from "../../actions/paymentActions";
import Meta from "../../components/Meta";
import { PAYMENT_PROVIDERS } from "../../constants/paymentConstants";
import { getSafeUserMessage } from "../../utils/errorMessages";

import "../orders/OrderScreen.css";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeErrorMessage(error, fallback = "Payment could not be updated.") {
  return getSafeUserMessage(error?.normalizedMessage || error?.message, fallback);
}

export function PayPalReturnScreen({ cancelled = false }) {
  const { orderNumber: routeOrderNumber = "" } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true,
    error: "",
  });

  const orderNumber = useMemo(
    () =>
      safeText(routeOrderNumber) ||
      safeText(searchParams.get("orderNumber")) ||
      safeText(searchParams.get("order")),
    [routeOrderNumber, searchParams],
  );

  useEffect(() => {
    let active = true;

    async function finishPayPalFlow() {
      if (!orderNumber) {
        setState({
          loading: false,
          error: "We could not match this PayPal return to an order.",
        });
        return;
      }

      try {
        if (cancelled) {
          await dispatch(
            cancelPendingOrderPayment({
              orderNumber,
              reason: "Customer cancelled PayPal checkout.",
            }),
          );

          if (!active) return;

          toast.info("PayPal checkout was cancelled. Your basket is unchanged.");
          navigate("/checkout", { replace: true });
          return;
        }

        const confirmed = await dispatch(
          confirmPaymentForExistingOrder({
            orderNumber,
            provider: PAYMENT_PROVIDERS.PAYPAL,
            paymentProvider: PAYMENT_PROVIDERS.PAYPAL,
          }),
        );

        if (!active) return;

        const confirmedOrderNumber = safeText(
          confirmed?.orderNumber || confirmed?.order?.orderNumber || orderNumber,
        );

        navigate(`/order/${encodeURIComponent(confirmedOrderNumber)}/success`, {
          replace: true,
        });
      } catch (error) {
        if (!active) return;

        const message = normalizeErrorMessage(
          error,
          cancelled
            ? "PayPal checkout could not be cancelled."
            : "PayPal payment could not be completed.",
        );

        setState({ loading: false, error: message });
        toast.error(message);
      }
    }

    finishPayPalFlow();

    return () => {
      active = false;
    };
  }, [cancelled, dispatch, navigate, orderNumber]);

  return (
    <main className="order-detail">
      <Meta
        title={
          cancelled
            ? "PayPal checkout cancelled | Academy Costumes London"
            : "Completing PayPal payment | Academy Costumes London"
        }
        robots="noindex, nofollow"
      />

      <section className="order-detail__empty">
        {state.loading ? (
          <>
            <Loader2 className="order-detail__spin" />
            <h1>{cancelled ? "Returning to checkout" : "Checking payment"}</h1>
            <p>Please wait while we retrieve the latest server status.</p>
          </>
        ) : (
          <>
            {state.error ? <XCircle size={28} /> : <CheckCircle2 size={28} />}
            <h1>{cancelled ? "PayPal checkout cancelled" : "Payment update"}</h1>
            <p>{state.error || "Your payment status is being confirmed."}</p>
            <div className="order-detail__inline-actions">
              <Link className="order-detail__button" to="/checkout">
                Return to checkout
              </Link>
              <Link className="order-detail__button order-detail__button--dark" to="/orders">
                View orders
              </Link>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export function PayPalCancelScreen() {
  return <PayPalReturnScreen cancelled />;
}

export default PayPalReturnScreen;
